/**
 * Collection Chat — shared request/response types for the "ask about this
 * collection" surface (POST /v1/assistant/collection-chat).
 *
 * Distinct from /v1/assistant/query (the open-ended recall tab) and from
 * /v1/assistant (the full capture/recall/explore pipeline). This surface
 * is scoped: the conversation always runs inside one Collection, with
 * that Collection's blocks loaded as the LLM's context. The frontend
 * holds the chat history (stateless backend) and replays it on each
 * turn.
 */
import { z } from "zod";
import { prefixedIdSchema } from "./id";
/** Role of a chat turn. Matches the conventional LLM-message vocabulary. */
export const CollectionChatRoleSchema = z.enum(["user", "assistant"]);
/**
 * One past turn in the conversation. The frontend keeps the canonical
 * transcript and sends the prior turns on each request — the server
 * does not persist anything.
 */
export const CollectionChatTurnSchema = z.object({
    role: CollectionChatRoleSchema,
    content: z.string().min(1).max(4000),
});
/** Max prior turns the client may replay in one request. */
export const MAX_COLLECTION_CHAT_HISTORY = 20;
/**
 * Max blocks the ad-hoc `blockIds` variant may include in one request.
 * Mirrors the server-side `MAX_BLOCKS` cap so a client that asks for more
 * is rejected at validation time rather than silently truncated. The
 * smart-collection use case (top-12 results + ~18 overflow) sits well
 * under this ceiling; the cap is here to prevent a misbehaving caller
 * from blowing prompt size out by passing hundreds of ids.
 */
export const MAX_COLLECTION_CHAT_BLOCK_IDS = 50;
/**
 * Common request fields shared by both invocation modes.
 *
 * Split into a base so the union variants can extend without duplicating
 * the `message` / `history` shape. `.passthrough()` is intentionally
 * NOT used — we want strict validation; extra keys are stripped by Zod.
 */
const CollectionChatRequestBaseSchema = z.object({
    message: z.string().min(1).max(2000),
    history: z.array(CollectionChatTurnSchema).max(MAX_COLLECTION_CHAT_HISTORY).optional(),
});
/**
 * Two invocation modes:
 *
 *   1. **Scoped to a persisted Collection** (`collectionId`) — the original
 *      "ask about this Collection" surface. Server loads the Collection's
 *      blocks itself; the title comes from the persisted record.
 *   2. **Ad-hoc block set** (`blockIds` + `title`) — for transient surfaces
 *      like the smart-collection tab opened by the omnibar's
 *      "Ask anything…" flow. The server skips the Collection lookup and
 *      loads exactly the blocks the caller listed (still filtered by the
 *      caller's read ACL — `BlockEntity.list` enforces this regardless of
 *      how the ids were supplied). `title` is needed because there's no
 *      Collection record to read it from; it becomes the value of the
 *      prompt's `<collection_title>` tag.
 *
 * Union (not discriminated by a literal `type` field) because there's no
 * natural discriminator and the two field sets are themselves distinguishable.
 */
export const CollectionChatRequestSchema = z.union([
    CollectionChatRequestBaseSchema.extend({
        collectionId: prefixedIdSchema("collection"),
    }),
    CollectionChatRequestBaseSchema.extend({
        blockIds: z
            .array(prefixedIdSchema("block"))
            .min(1)
            .max(MAX_COLLECTION_CHAT_BLOCK_IDS),
        /** Free-form label rendered into the `<collection_title>` prompt tag. */
        title: z.string().min(1).max(120),
    }),
]);
